(ns global.endor.checkout.stripe.core
  (:require ["@stripe/react-stripe-js/checkout" :as react-stripe-checkout]
            ["@stripe/stripe-js" :as stripe]
            ["react-phone-number-input" :as react-phone-number-input]
            [re-frame.core :as rf]
            [re-frame.uix :refer [use-subscribe]]
            [uix.core :as uix :refer [$ defui]]
            [global.endor.checkout.router :as router]
            [global.endor.checkout.stripe.ui :as stripe.ui]
            [global.endor.checkout.stripe.config :as stripe.config]
            [global.endor.checkout.api.request :as api.request]
            [global.endor.checkout.ui.components.basics :as ui]
            [global.endor.checkout.ui.components.waiting :as waiting]
            [cljs-bean.core :as bean]
            [global.endor.checkout.utils :as utils]))

(def APP_STORE_LINK "https://apps.apple.com/app/endor-global/id6744930227")

(def user-locations
  [{:label "Norway"
    :value "NO"
    :currency "nok"}
   {:label "Europe"
    :value "EU"
    :currency "eur"}
   {:label "United Kingdom"
    :value "UK"
    :currency "gbp"}
   {:label "United States"
    :value "US"
    :currency "usd"}])

(def straps
  [{:strap-code "strap-black-360"
    :strap-color "#000" :strap-text "Endor Black 360"
    :description "Standard sports band in black fabric. For high and low intensity workouts."}
   {:strap-code "strap-beige"
    :strap-color "#CEBAA3" :strap-text "Endor Beige"
    :description "Grounded and bold. Made for balance, power and performance in highly durable and waterproof FKM rubber."}
   #_{:strap-code "strap-brown"
    :strap-color "#342A22" :strap-text "Endor Brown"
    :description "Light and resilient. Built for action, breath, and stillness in highly durable and waterproof FKM rubber."}
   {:strap-code  "strap-black"
    :strap-color "#080808" :strap-text "Endor Black"
    :description "Sleek and durable. For training, recovery, and everyday movement in waterproof FKM rubber."}])

(def initial-state
  {:phase :landing
   :user-location "NO"
   :straps straps
   :selected-strap (first straps)
   :privacy-terms-accepted false
   :checkout {:api-status :init
              :session nil
              :focused-step :contact
              :steps {:order {:title "Order details"
                              :index 0}
                      :contact {:title "Contact information"
                                :index 1}
                      :shipping {:title "Shipping information"
                                 :index 2
                                 :locked true}
                      :payment {:title "Payment method"
                                :index 3
                                :locked true}}}})

(defn get-subscription-type [route]
  (get-in route [:query-params :subscription] "monthly"))

(rf/reg-event-fx
 ::init
 (fn [{:keys [db]} _]
   (println "init-stripe")
   {:db (assoc db ::db initial-state)}))

(rf/reg-sub
 ::stripe
 (fn [db _]
   (get db ::db)))

(rf/reg-sub
 ::checkout
 :<- [::stripe]
 (fn [stripe _]
   (:checkout stripe)))

(do :session

    (rf/reg-event-fx
     ::create-checkout-session
     (fn [{:keys [db]} _]
       (let [route (get-in db [::router/db :route])
             subscription-type (get-subscription-type route)
             user-location (get-in db [::db :user-location])
             user-location->currency (into {} (map (juxt :value :currency)) user-locations)
             currency (get user-location->currency user-location)
             strap-code (get-in db [::db :selected-strap :strap-code])]
         {:db (assoc-in db [::db :checkout :session] nil)
          :dispatch
          [::api.request/fetch {:id :create-checkout-session
                                :url "/api/v1/stripe/subscription-checkout"
                                :method :post
                                :body {:return-url (str js/window.location.origin "/checkout-complete?session_id={CHECKOUT_SESSION_ID}")
                                       :price-ids (cond
                                                    (= "yearly" subscription-type) [(-> stripe.config/config :prices :yearly-subscription)]
                                                    (= "monthly" subscription-type) [(-> stripe.config/config :prices :monthly-subscription)]
                                                    :else [])
                                       :currency currency
                                       :user-location user-location
                                       :subscription-type subscription-type
                                       :strap-code strap-code}
                                :on-success [::session-success]
                                :on-failure [::session-failure]}]})))

    (rf/reg-event-fx
     ::retrieve-checkout-session
     (fn [{:keys [db]} [_ session-id]]
       (when session-id
         {:db (assoc-in db [::db :checkout :session] nil)
          :dispatch
          [::api.request/fetch {:id :retrieve-checkout-session
                                :url (str "/api/v1/stripe/subscription-checkout?session-id=" session-id)
                                :method :get
                                :on-success [::session-success]
                                :on-failure [::session-failure]}]})))

    (rf/reg-event-db
     ::session-success
     (fn [db [_ res]]
       (update-in db
                  [::db :checkout]
                  merge
                  {:session (:body res)
                   :api-status :success
                   :error nil})))

    (rf/reg-event-db
     ::session-failure
     (fn [db [_ e]]
       (update-in db
                  [::db :checkout]
                  merge
                  {:session nil
                   :api-status :failure
                   :error e}))))

(rf/reg-event-db
 ::set-phase
 (fn [db [_ phase]]
   (assoc-in db [::db :phase] phase)))

(rf/reg-event-fx
 ::set-selected-strap
 (fn [{:keys [db]} [_ strap]]
   {:db (assoc-in db [::db :selected-strap] strap)
    :dispatch [::create-checkout-session]}))

(rf/reg-event-fx
 ::back-to-landing
 (fn [{:keys [db]} _]
   {:db (assoc-in db [::db :checkout] (initial-state :checkout))
    :fx [[:dispatch [::set-phase :landing]]
         [:dispatch [::create-checkout-session]]]}))

(do :checkout-steps

    (rf/reg-sub
     ::current-step
     :<- [::checkout]
     (fn [{:keys [focused-step steps]} _]
       (get steps focused-step)))

    (rf/reg-event-db
     ::next-step
     (fn [db _]
       (let [steps (get-in db [::db :checkout :steps])
             focused (get-in db [::db :checkout :focused-step])
             current-idx (get-in steps [focused :index])
             next-step (->> steps
                            (filter (fn [[_ {:keys [index]}]] (= index (inc current-idx))))
                            (first)
                            (key))]
         (-> db
             (assoc-in [::db :checkout :focused-step] next-step)
             (assoc-in [::db :checkout :steps next-step :locked] false)))))

    (rf/reg-event-db
     ::focus-step
     (fn [db [_ step-kw]]
       (let [steps (get-in db [::db :checkout :steps])]
         (when-not (get-in steps [step-kw :locked])
           (assoc-in db [::db :checkout :focused-step] step-kw)))))

    (rf/reg-sub
     ::steps
     :<- [::checkout]
     (fn [{:keys [steps focused-step]} _]
       (let [ret (->> steps
                      (map
                       (fn [[key step]]
                         [key (assoc step :focused (= key focused-step) :key step)]))
                      (into {}))]
         (println ret)
         ret))))

(rf/reg-event-fx
 ::set-user-location
 (fn [{:keys [db]} [_ country-code]]
   {:db (cond-> (assoc-in db [::db :user-location] country-code)
          (= country-code "US") (assoc-in [::db :selected-strap]
                                          (first (get-in db [::db :straps]))))
    :dispatch [::create-checkout-session]}))

(rf/reg-event-db
 ::accept-privacy-terms
 (fn [db [_ value]]
   (assoc-in db [::db :privacy-terms-accepted] value)))

(def stripe-promise
  (stripe/loadStripe (stripe.config/config :publishable-key)))

(defui checkout []
  (let [stripe-checkout (react-stripe-checkout/useCheckout)
        stripe-checkout (.-checkout stripe-checkout)
        {:keys [phase checkout user-location]} (use-subscribe [::stripe])
        subscription-type (get-subscription-type (use-subscribe [::router/route]))
        session (:session checkout)
        steps (use-subscribe [::steps])
        allowed-countries (get-in session [:shipping_address_collection :allowed_countries])
        next-step #(rf/dispatch [::next-step])
        focus-step #(rf/dispatch [::focus-step %])]

    ($ :<>

       ($ stripe.ui/header
          {:label "Checkout"
           :on-back #(rf/dispatch [::back-to-landing])})

       (when (= phase :processing)
         ($ waiting/waiting-screen
            {:message "Processing payment"}))

       ($ ui/column
          {:gap :large}

          ($ ui/column
             {:gap :large}

             ($ stripe.ui/checkout-item
                (merge (:order steps)
                       {:complete true
                        :on-next next-step
                        :on-focus #(focus-step :order)})
                ($ stripe.ui/order-details
                   {:location-options user-locations
                    :user-location user-location
                    :on-change-location #(rf/dispatch [::set-user-location %])
                    :subscription-type subscription-type}))

             (let [[email set-email] (uix.core/use-state "")
                   [email-complete set-email-complete] (uix.core/use-state nil)
                   [phone set-phone] (uix.core/use-state "")
                   [phone-complete set-phone-complete] (uix.core/use-state nil)]
               ($ stripe.ui/checkout-item
                  (merge (:contact steps)
                         {:complete (and email-complete phone-complete)
                          :on-next next-step
                          :on-focus #(focus-step :contact)})
                  ($ stripe.ui/email-input
                     {:label "Enter your email address"
                      :is-valid email-complete
                      :value email
                      :autofocus (get-in steps [:contact :focused])
                      :on-change (fn [value]
                                   (set-email value)
                                   (-> (.updateEmail stripe-checkout value)
                                       (.then #(set-email-complete (= "success" (.-type %))))))})
                  ($ stripe.ui/phone-input
                     {:is-valid phone-complete
                      :value phone
                      :allowed-countries allowed-countries
                      :on-change (fn [value]
                                   (set-phone value)
                                   (if (and value (react-phone-number-input/isValidPhoneNumber value))
                                     (-> (.updatePhoneNumber stripe-checkout value)
                                         (.then #(set-phone-complete (= "success" (.-type %))))
                                         (.catch #(set-phone-complete false)))
                                     (set-phone-complete false)))})))

             (let [[address-complete set-address-complete] (uix.core/use-state nil)]
               ($ stripe.ui/checkout-item
                  (merge (:shipping steps)
                         {:complete address-complete
                          :on-next next-step
                          :on-focus #(focus-step :shipping)})
                  ($ react-stripe-checkout/ShippingAddressElement
                     (bean/->js {:options {:mode "shipping"}
                                 :onChange (fn [event]
                                             (let [is-complete (.-complete event)]
                                               (set-address-complete is-complete)
                                               (when (and is-complete stripe-checkout (.-value event))
                                                 (-> (.updateBillingAddress stripe-checkout (.-value event))
                                                     (.then #(println "Billing address updated successfully"))
                                                     (.catch #(println "Failed to update billing address:" %))))))}))))

             (let [[terms-accepted set-terms-accepted] (uix.core/use-state nil)
                   [return-terms-accepted set-return-terms-accepted] (uix.core/use-state nil)
                   [payment-complete set-payment-complete] (uix.core/use-state nil)]
               ($ stripe.ui/checkout-item
                  (merge (:payment steps)
                         {:button-text "Place order"
                          :complete (and terms-accepted
                                         payment-complete
                                         (or (= "yearly" subscription-type) return-terms-accepted))
                          :on-focus #(focus-step :payment)
                          :on-next (fn [event]
                                     (.preventDefault event)
                                     (-> (.confirm stripe-checkout #js {:redirect "if_required"})
                                         (.then (fn [_]
                                                  (rf/dispatch [::retrieve-checkout-session (:id session)])
                                                  (rf/dispatch [::router/navigate :checkout-complete])))
                                         (.catch (fn [_]
                                                   (rf/dispatch [::session-failure "Unable to confirm payment."])
                                                   (rf/dispatch [::router/navigate :checkout-complete]))))
                                     (rf/dispatch [::set-phase :processing]))})
                  ($ stripe.ui/order-summary
                     {:subscription-type subscription-type})
                  ($ react-stripe-checkout/PaymentElement
                     (bean/->js {:options {:layout {:type "tabs"
                                                    :defaultCollapsed false}
                                           :fields {:billingDetails {:name "never"
                                                                     :email "never"
                                                                     :phone "never"
                                                                     :address "never"}}}
                                 :onChange #(set-payment-complete (.-complete %))}))
                  (when (= subscription-type "monthly")
                    ($ stripe.ui/device-return-agreement
                       {:accepted return-terms-accepted
                        :subscription-type subscription-type
                        :set-accepted set-return-terms-accepted}))
                  ($ stripe.ui/term-of-use-agreement
                     {:accepted terms-accepted
                      :subscription-type subscription-type
                      :set-accepted set-terms-accepted})))

             ($ stripe.ui/ios-only)
             (when (= "monthly"
                      subscription-type)
               ($ stripe.ui/faqs)))))))

(defn- await-some
  [val]
  (if (some? val)
    (js/Promise.resolve val)
    (js/Promise. (fn []))))

(defui stripe []
  (let [{:keys [phase user-location privacy-terms-accepted straps selected-strap]} (use-subscribe [::stripe])
        {:keys [api-status session]} (use-subscribe [::checkout])
        subscription-type (get-subscription-type (use-subscribe [::router/route]))]
    ($ stripe.ui/container
       (if (= api-status :failure)
         ($ ui/text {:font :h1} "API failure")

         ($ react-stripe-checkout/CheckoutProvider
            (bean/->js {;; Using checkout session ID as key, in order to re-create CheckoutProvider
                        ;; This is because we create a new checkout session when selecting strap and location, for example
                        ;; Newly created checkout sessions seem not to be picked up by Stripe React hook
                        :key (:id session)
                        :stripe stripe-promise
                        :options {:clientSecret (await-some (:client_secret session))
                                  :elementsOptions {:appearance {:theme "night" :variables {}}}}})

            (case phase
              :landing
              ($ :<>
                 ($ stripe.ui/header
                    {:label "Membership"
                     :on-back (fn [] (set! js/window.location.href "https://www.endor.global/"))})
                 ($ stripe.ui/checkout-landing
                    {:user-location user-location
                     :straps straps
                     :selected-strap selected-strap
                     :location-options user-locations
                     :on-change-location #(rf/dispatch [::set-user-location %])
                     :privacy-terms-accepted privacy-terms-accepted
                     :session-ready (boolean (:client_secret session))
                     :on-accept-privacy-terms #(rf/dispatch [::accept-privacy-terms %])
                     :on-continue #(rf/dispatch [::set-phase :checkout])
                     :subscription-type subscription-type}))

              (:processing :checkout)
              ($ checkout)))))))

(defui complete []
  (let [{:keys [session error]} (use-subscribe [::checkout])]
    (uix.core/use-effect (fn []
                           (when-let [session-id (:session_id (utils/get-query-params))]
                             (rf/dispatch [::retrieve-checkout-session session-id])))
                         [])
    (cond
      (not session) ($ waiting/waiting-screen {:message "Checking payment..."})

      (= "complete" (:status session))
      (if (= "paid" (:payment_status session))
        ($ stripe.ui/payment-success
           {:goto-app-store #(set! (.-location js/window) APP_STORE_LINK)})
        ($ stripe.ui/payment-failure
           {:on-navigate-to-checkout #(rf/dispatch [::router/navigate :stripe])}))

      error
      ($ stripe.ui/checkout-error
         {:error error
          :on-navigate-to-checkout #(rf/dispatch [::router/navigate :stripe])})

      :else nil)))
